import { useEffect, useRef, useState } from "react";
import jscanify from "jscanify/client";

/** THEME-1b · 画布上的取景框 / 角点用主色：canvas 读不了 Tailwind 类，取 <html> 上的主题变量（classic 默认 = #2563EB，一个像素不变）。 */
function brandColor(): string {
  try {
    const t = getComputedStyle(document.documentElement).getPropertyValue("--c-blue-600").trim();
    if (t) return `rgb(${t})`;
  } catch {
    /* 取不到就用默认蓝 */
  }
  return "#2563EB";
}


/**
 * 凭证扫描仪模式（拿来主义：jscanify npm 打包 + OpenCV.js CDN·不自研图像算法）。
 * 对标 Genius Scan / 美国银行支票扫描：
 *   实时取景（绿框自动吸附跟随票据）→ 稳定后自动/手动拍摄 → 透视拉正 → B&W 二值化（传真黑白·文件小）→ 上传。
 * 手动拖角 = 自动框不准时的兜底（低对比度单据）。文件选择 = 无相机/桌面端兜底。
 * 运行期需全局 window.cv（OpenCV.js·CDN 4.8.0）；jscanify 已打包进 bundle。
 */
const OPENCV_URL = "https://docs.opencv.org/4.8.0/opencv.js";
const DETECT_W = 480; // 逐帧检测降采样宽度（全分辨率太慢）

type Pt = { x: number; y: number };
type Corners = { topLeftCorner: Pt; topRightCorner: Pt; bottomRightCorner: Pt; bottomLeftCorner: Pt };
const ORDER: (keyof Corners)[] = ["topLeftCorner", "topRightCorner", "bottomRightCorner", "bottomLeftCorner"];

function loadScript(src: string, timeoutMs = 30000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; reject(new Error("加载超时: " + src)); } }, timeoutMs);
    s.src = src; s.async = true;
    s.onload = () => { if (!done) { done = true; clearTimeout(t); resolve(); } };
    s.onerror = () => { if (!done) { done = true; clearTimeout(t); s.remove(); reject(new Error("加载失败: " + src)); } };
    document.head.appendChild(s);
  });
}
let _ready: Promise<void> | null = null;
function loadCv(): Promise<void> {
  if (_ready) return _ready;
  _ready = (async () => {
    try {
      await loadScript(OPENCV_URL);
      const cv = (window as any).cv;
      await new Promise<void>((res, rej) => {
        if (cv && cv.Mat) return res();
        const to = setTimeout(() => rej(new Error("OpenCV 初始化超时")), 30000);
        (window as any).cv.onRuntimeInitialized = () => { clearTimeout(to); res(); };
      });
    } catch (e) { _ready = null; throw e; }
  })();
  return _ready;
}

const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);
const validCorners = (c: any): c is Corners =>
  c && ORDER.every((k) => c[k] && typeof c[k].x === "number" && typeof c[k].y === "number");
// 四边形面积（shoelace）·过滤噪声小框
function quadArea(c: Corners): number {
  const p = ORDER.map((k) => c[k]);
  let a = 0;
  for (let i = 0; i < 4; i++) { const q = p[i], r = p[(i + 1) % 4]; a += q.x * r.y - r.x * q.y; }
  return Math.abs(a) / 2;
}

// jscanify 检测四角（src 可为 canvas/image）·失败返回 null
function autoCorners(src: HTMLCanvasElement | HTMLImageElement, scanner: jscanify): Corners | null {
  const cv = (window as any).cv;
  let mat: any, contour: any;
  try {
    mat = cv.imread(src);
    contour = scanner.findPaperContour(mat);
    if (!contour) return null;
    const c = scanner.getCornerPoints(contour);
    return validCorners(c) ? c : null;
  } catch { return null; }
  finally { [mat, contour].forEach((m) => { try { m && m.delete && m.delete(); } catch { /* noop */ } }); }
}
function defaultCorners(W: number, H: number): Corners {
  return {
    topLeftCorner: { x: W * 0.08, y: H * 0.08 }, topRightCorner: { x: W * 0.92, y: H * 0.08 },
    bottomRightCorner: { x: W * 0.92, y: H * 0.92 }, bottomLeftCorner: { x: W * 0.08, y: H * 0.92 },
  };
}

type Step = "idle" | "loading" | "camera" | "adjust" | "uploading";

export function ScannerCapture({
  open, onClose, onUploaded, upload,
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: (url: string, key: string) => void;
  upload: (file: Blob, filename: string) => Promise<{ url: string; key: string }>;
}) {
  const [step, setStep] = useState<Step>("idle");
  const [error, setError] = useState<string | null>(null);
  const [bw, setBw] = useState(true);     // 默认黑白（传真扫描格式）
  const [auto, setAuto] = useState(true); // 稳定后自动拍摄
  const [locked, setLocked] = useState(false); // 已识别到单据（UI 提示）

  const scannerRef = useRef<jscanify | null>(null);
  const srcCanvasRef = useRef<HTMLCanvasElement | null>(null); // 拍下的全分辨率原图
  const cornersRef = useRef<Corners | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);  // adjust 用
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null); // camera 取景框
  const detectCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef(0);
  const frameRef = useRef(0);
  const stableRef = useRef(0);
  const lastQuadRef = useRef<Corners | null>(null);
  const scaleRef = useRef(1);
  const dragRef = useRef<keyof Corners | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const autoRef = useRef(auto);
  autoRef.current = auto;

  useEffect(() => { if (step === "adjust") draw(); }, [step]);
  useEffect(() => { if (!open) { stopCamera(); reset(); } return () => { stopCamera(); }; }, [open]);
  if (!open) return null;

  function getScanner() { if (!scannerRef.current) scannerRef.current = new jscanify(); return scannerRef.current; }
  function getDetectCanvas() { if (!detectCanvasRef.current) detectCanvasRef.current = document.createElement("canvas"); return detectCanvasRef.current; }

  // ── 实时相机 ──────────────────────────────────────────────
  async function startCamera() {
    setError(null); setStep("loading");
    try {
      await loadCv();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      setStep("camera");
      // 等 video 节点挂载
      requestAnimationFrame(async () => {
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play().catch(() => {});
        frameRef.current = 0; stableRef.current = 0; lastQuadRef.current = null;
        loop();
      });
    } catch (err) {
      setError("相机打开失败：" + (err as Error).message + "（可改用「从文件选择」）");
      setStep("idle");
    }
  }
  function stopCamera() {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }
  function loop() {
    const v = videoRef.current, overlay = overlayRef.current;
    if (!v || !overlay) { rafRef.current = requestAnimationFrame(loop); return; }
    const vw = v.videoWidth, vh = v.videoHeight;
    if (vw && vh) {
      // overlay 尺寸贴合 video 显示尺寸
      const dispW = v.clientWidth, dispH = v.clientHeight;
      if (overlay.width !== dispW || overlay.height !== dispH) { overlay.width = dispW; overlay.height = dispH; }
      const ctx = overlay.getContext("2d")!;
      ctx.clearRect(0, 0, overlay.width, overlay.height);
      // 每 2 帧检测一次
      frameRef.current++;
      if (frameRef.current % 2 === 0) lastQuadRef.current = detectQuad(v);
      const q = lastQuadRef.current;
      if (q) {
        const sx = dispW / vw, sy = dispH / vh;
        ctx.strokeStyle = "#22c55e"; ctx.lineWidth = 3; ctx.fillStyle = "rgba(34,197,94,0.14)";
        ctx.beginPath();
        ORDER.forEach((k, i) => { const p = q[k]; const x = p.x * sx, y = p.y * sy; i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
        ctx.closePath(); ctx.fill(); ctx.stroke();
        if (!locked) setLocked(true);
        stableRef.current++;
        if (autoRef.current && stableRef.current >= 14) { doCapture(); return; }
      } else {
        stableRef.current = 0;
        if (locked) setLocked(false);
      }
    }
    rafRef.current = requestAnimationFrame(loop);
  }
  // 降采样检测·返回全分辨率 video 坐标的四角
  function detectQuad(v: HTMLVideoElement): Corners | null {
    const vw = v.videoWidth, vh = v.videoHeight;
    if (!vw) return null;
    const scale = DETECT_W / vw;
    const dc = getDetectCanvas();
    dc.width = DETECT_W; dc.height = Math.max(1, Math.round(vh * scale));
    dc.getContext("2d")!.drawImage(v, 0, 0, dc.width, dc.height);
    const c = autoCorners(dc, getScanner());
    if (!c) return null;
    const up = (p: Pt) => ({ x: p.x / scale, y: p.y / scale });
    const full: Corners = {
      topLeftCorner: up(c.topLeftCorner), topRightCorner: up(c.topRightCorner),
      bottomRightCorner: up(c.bottomRightCorner), bottomLeftCorner: up(c.bottomLeftCorner),
    };
    return quadArea(full) > vw * vh * 0.12 ? full : null; // 太小当噪声丢弃
  }
  function doCapture() {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    cancelAnimationFrame(rafRef.current);
    const cap = document.createElement("canvas");
    cap.width = v.videoWidth; cap.height = v.videoHeight;
    cap.getContext("2d")!.drawImage(v, 0, 0);
    const lastQuad = lastQuadRef.current;
    stopCamera();
    srcCanvasRef.current = cap;
    cornersRef.current = autoCorners(cap, getScanner()) ?? lastQuad ?? defaultCorners(cap.width, cap.height);
    setLocked(false);
    setStep("adjust");
  }

  // ── 文件兜底 ──────────────────────────────────────────────
  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null); setStep("loading");
    try {
      await loadCv();
      const img = new Image();
      img.src = URL.createObjectURL(f);
      await img.decode();
      const cap = document.createElement("canvas");
      cap.width = img.naturalWidth; cap.height = img.naturalHeight;
      cap.getContext("2d")!.drawImage(img, 0, 0);
      srcCanvasRef.current = cap;
      cornersRef.current = autoCorners(cap, getScanner()) ?? defaultCorners(cap.width, cap.height);
      setStep("adjust");
    } catch (err) {
      setError("扫描引擎加载失败：" + (err as Error).message + "（请检查网络后重试）");
      setStep("idle");
    }
  }

  // ── 手动拖角（兜底）──────────────────────────────────────
  function draw() {
    const src = srcCanvasRef.current, canvas = canvasRef.current, c = cornersRef.current;
    if (!src || !canvas || !c) return;
    const maxW = Math.min(window.innerWidth - 80, 640);
    const scale = maxW / src.width;
    scaleRef.current = scale;
    canvas.width = src.width * scale;
    canvas.height = src.height * scale;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = brandColor(); ctx.lineWidth = 2;
    ctx.beginPath();
    ORDER.forEach((k, i) => { const p = c[k]; const x = p.x * scale, y = p.y * scale; i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
    ctx.closePath(); ctx.stroke();
    ORDER.forEach((k) => { const p = c[k]; ctx.fillStyle = brandColor(); ctx.beginPath(); ctx.arc(p.x * scale, p.y * scale, 9, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(p.x * scale, p.y * scale, 4, 0, Math.PI * 2); ctx.fill(); });
  }
  function evtPt(e: React.PointerEvent): Pt {
    const r = canvasRef.current!.getBoundingClientRect();
    const sx = canvasRef.current!.width / r.width;
    return { x: (e.clientX - r.left) * sx / scaleRef.current, y: (e.clientY - r.top) * sx / scaleRef.current };
  }
  function onDown(e: React.PointerEvent) {
    const p = evtPt(e), c = cornersRef.current!;
    let best: keyof Corners | null = null, bd = 1e9;
    for (const k of ORDER) { const d = dist(p, c[k]); if (d < bd) { bd = d; best = k; } }
    if (best && bd < 60 / scaleRef.current) { dragRef.current = best; canvasRef.current!.setPointerCapture(e.pointerId); }
  }
  function onMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    const src = srcCanvasRef.current!;
    const p = evtPt(e);
    cornersRef.current![dragRef.current] = { x: Math.max(0, Math.min(src.width, p.x)), y: Math.max(0, Math.min(src.height, p.y)) };
    draw();
  }
  function onUp() { dragRef.current = null; }

  // ── 拉正 + 二值化 + 上传 ─────────────────────────────────
  async function useResult() {
    const src = srcCanvasRef.current, c = cornersRef.current;
    if (!src || !c) return;
    setError(null); setStep("uploading");
    try {
      const scanner = getScanner();
      const outW = Math.round((dist(c.topLeftCorner, c.topRightCorner) + dist(c.bottomLeftCorner, c.bottomRightCorner)) / 2);
      const outH = Math.round((dist(c.topLeftCorner, c.bottomLeftCorner) + dist(c.topRightCorner, c.bottomRightCorner)) / 2);
      const result = scanner.extractPaper(src, outW || src.width, outH || src.height, c);
      if (!result) throw new Error("透视拉正失败");
      const mime = bw ? "image/png" : "image/jpeg";   // 黑白用 PNG（位图压缩小）·原色用 JPEG
      const name = bw ? "scan.png" : "scan.jpg";
      if (bw) applyBW(result); else applyEnhance(result);
      const blob = await new Promise<Blob | null>((res) => result.toBlob((b) => res(b), mime, 0.92));
      if (!blob) throw new Error("生成图片失败");
      const r = await upload(blob, name);
      onUploaded(r.url, r.key);
      reset();
    } catch (err) {
      setError("裁切/上传失败：" + (err as Error).message);
      setStep("adjust");
    }
  }
  // B&W 二值化：自适应阈值 → 纯黑白传真格式（文件小）
  function applyBW(canvas: HTMLCanvasElement) {
    const cv = (window as any).cv;
    let src, gray, dst;
    try {
      src = cv.imread(canvas);
      gray = new cv.Mat(); cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
      dst = new cv.Mat();
      cv.adaptiveThreshold(gray, dst, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, 25, 12);
      cv.imshow(canvas, dst);
    } finally { [src, gray, dst].forEach((m) => { try { m && m.delete(); } catch { /* noop */ } }); }
  }
  // 原色增强：灰度+对比（保留彩色选项时用）
  function applyEnhance(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d")!;
    const d = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const px = d.data;
    for (let i = 0; i < px.length; i += 4) {
      const g = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      const v = Math.max(0, Math.min(255, (g - 128) * 1.3 + 128 + 8));
      px[i] = px[i + 1] = px[i + 2] = v;
    }
    ctx.putImageData(d, 0, 0);
  }

  function reset() {
    setStep("idle"); setError(null); setLocked(false);
    srcCanvasRef.current = null; cornersRef.current = null; lastQuadRef.current = null;
    if (fileRef.current) fileRef.current.value = "";
  }
  function close() { stopCamera(); reset(); onClose(); }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4" onClick={close}>
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-[16px] font-bold text-slate-900">📷 扫描凭证</h2>
          <button onClick={close} className="text-2xl leading-none text-slate-400 hover:text-slate-700">×</button>
        </div>

        {step === "idle" && (
          <div className="flex flex-col gap-3">
            <button onClick={startCamera} className="flex cursor-pointer flex-col items-center gap-1.5 rounded-2xl border-2 border-dashed border-blue-300 bg-blue-50/40 py-10 hover:bg-blue-50">
              <span className="text-4xl">📷</span>
              <span className="text-[15px] font-semibold text-blue-600">实时扫描（推荐）</span>
              <span className="text-[12px] text-slate-500">对准单据 · 绿框自动吸附 · 稳定后自动拍摄</span>
            </button>
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-300 py-3 text-[13px] font-medium text-slate-600 hover:bg-slate-50">
              🖼 从文件 / 相册选择
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
            </label>
            {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{error}</div>}
          </div>
        )}

        {step === "loading" && <div className="py-16 text-center text-slate-500">加载扫描引擎…（首次约 8MB·稍候）</div>}

        {step === "camera" && (
          <div className="flex flex-1 flex-col gap-3 overflow-hidden">
            <div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-lg bg-black">
              <video ref={videoRef} muted playsInline className="max-h-[60vh] w-full object-contain" />
              <canvas ref={overlayRef} className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
              <div className={`absolute left-1/2 top-3 -translate-x-1/2 rounded-full px-3 py-1 text-[12px] font-semibold ${locked ? "bg-emerald-500 text-white" : "bg-black/50 text-white"}`}>
                {locked ? "✓ 已识别单据" : "对准单据…"}
              </div>
            </div>
            <div className="flex items-center justify-between text-[13px] text-slate-700">
              <label className="flex items-center gap-2"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> 稳定后自动拍摄</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={bw} onChange={(e) => setBw(e.target.checked)} /> 黑白扫描件</label>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { stopCamera(); reset(); }} className="rounded-lg border border-slate-300 px-4 py-2.5 text-[13px] font-medium hover:bg-slate-50">返回</button>
              <button onClick={doCapture} className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:bg-blue-700">● 拍摄</button>
            </div>
          </div>
        )}

        {step === "adjust" && (
          <div className="flex flex-1 flex-col gap-3 overflow-hidden">
            <p className="text-[12px] text-slate-500">自动已框好 → 直接「完成」；不准就拖蓝色四角微调</p>
            <div className="flex flex-1 items-center justify-center overflow-auto">
              <canvas ref={canvasRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} className="touch-none rounded-lg border border-slate-200" />
            </div>
            {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{error}</div>}
            <label className="flex items-center gap-2 text-[13px] text-slate-700"><input type="checkbox" checked={bw} onChange={(e) => setBw(e.target.checked)} /> 黑白扫描件（传真格式·文件小）</label>
            <div className="flex gap-2">
              <button onClick={() => { reset(); }} className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-[13px] font-medium hover:bg-slate-50">重拍</button>
              <button onClick={useResult} className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-blue-700">✓ 完成并上传</button>
            </div>
          </div>
        )}

        {step === "uploading" && <div className="py-16 text-center text-slate-500">拉正 + 二值化 + 上传中…</div>}
      </div>
    </div>
  );
}
